var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, ThemeContext } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Navigation from './components/Layout/Navigation';
import { ExamInterface } from './components/Exams';
import EnhancedDocumentAnalysis from './components/Documents/DocumentAnalysis';
import ModernDocumentSessionLanding from './components/Documents/DocumentSessionLanding';
import ExamBoardsHub from './components/ExamBoards/ExamBoardsHub';
import BoardDashboard from './components/ExamBoards/BoardDashboard';
import Welcome from './components/Pages/Welcome';
import LoginPage from './components/Pages/LoginPage';
import SignupPage from './components/Pages/SignupPage';
import UserProfile from './components/Auth/UserProfile';
import LimitReachedModal from './components/UI/LimitReachedModal';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { LoadingSpinner } from './components/UI/LoadingSpinner';
import { Card, CardContent } from './components/UI/Card';
// Enhanced styles for document tabs
import './styles/documents/enhanced-tabs.css';
// Enhanced styles for navigation layout
import './styles/navigation-enhanced.css';
import { GraduationCap } from 'lucide-react';
import { getUsageSummary } from './services/apiClient';
import { routes, pathToPage } from './config/routes';
import NotFound from './components/Pages/NotFound';
import ActionHandler from './components/Auth/ActionHandler';
import { ToastProvider, useToast } from './components/UI/Toast';
import ErrorBoundary from './components/UI/ErrorBoundary';
import { getFriendlyErrorMessage, isJsonParsingError } from './lib/errorHandler';
import { initializeViewportHeight } from './lib/viewportUtils';
import CookieConsent from './components/CookieConsent';
import CookiePolicy from './components/Pages/CookiePolicy';
// Enhanced Document Analysis Screen - moved outside AppContent to prevent recreation
var EnhancedDocumentAnalysisScreen = function () {
    var navigate = useNavigate();
    var handleAuthAction = function (mode) {
        navigate("/".concat(mode));
    };
    console.log('[App.tsx] EnhancedDocumentAnalysisScreen rendered');
    return (_jsx(ProtectedRoute, __assign({ onRequestLogin: function () { return handleAuthAction('login'); } }, { children: _jsx(EnhancedDocumentAnalysis, {}) })));
};
// Main App content
var AppContent = function () {
    var theme = useContext(ThemeContext).theme;
    var _a = useAuth(), currentUser = _a.currentUser, loading = _a.loading;
    var showError = useToast().showError;
    var navigate = useNavigate();
    var location = useLocation();
    var currentPage = pathToPage(location.pathname);
    var _b = useState(false), showProfile = _b[0], setShowProfile = _b[1];
    var _c = useState(false), showLimitModal = _c[0], setShowLimitModal = _c[1];
    var _d = useState('requests'), limitType = _d[0], setLimitType = _d[1];
    var _e = useState('free'), currentPlan = _e[0], setCurrentPlan = _e[1];
    // Global error handler for unhandled errors
    useEffect(function () {
        var handleUnhandledRejection = function (event) {
            console.error('Unhandled promise rejection:', event.reason);
            if (isJsonParsingError(event.reason)) {
                showError(getFriendlyErrorMessage(event.reason));
                event.preventDefault();
            }
        };
        var handleError = function (event) {
            console.error('Uncaught error:', event.error);
            if (isJsonParsingError(event.error)) {
                showError(getFriendlyErrorMessage(event.error));
                event.preventDefault();
            }
        };
        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        window.addEventListener('error', handleError);
        return function () {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
            window.removeEventListener('error', handleError);
        };
    }, [showError]);
    // Initialize viewport height for mobile support
    useEffect(function () {
        var cleanup = initializeViewportHeight();
        return cleanup;
    }, []);
    // Show auth modal if user is not authenticated
    useEffect(function () {
        if (!loading && !currentUser && !['/login', '/signup', '/auth/action'].includes(location.pathname)) {
            navigate('/login');
        }
    }, [currentUser, loading, location.pathname, navigate]);
    // Fetch current plan when user changes
    useEffect(function () {
        var fetchPlan = function () { return __awaiter(void 0, void 0, void 0, function () {
            var usage, plan, error_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!currentUser) return [3 /*break*/, 4];
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, getUsageSummary()];
                    case 2:
                        usage = _b.sent();
                        plan = ((_a = usage.limits) === null || _a === void 0 ? void 0 : _a.plan) || 'free';
                        setCurrentPlan(plan);
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _b.sent();
                        console.warn('Could not fetch usage plan:', error_1);
                        setCurrentPlan('free');
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        fetchPlan();
    }, [currentUser]);
    // Listen for limit reached events from API client
    useEffect(function () {
        var handleLimitReached = function (event) {
            var _a = event.detail, type = _a.limitType, code = _a.code;
            // Determine limit type from error code
            var detectedType = 'requests';
            if (code === 'DAILY_TOKENS_LIMIT_REACHED') {
                detectedType = 'tokens';
            }
            else if (code === 'DAILY_REQUESTS_LIMIT_REACHED') {
                detectedType = 'requests';
            }
            else if (type === 'exam_analysis') {
                detectedType = 'exam_analysis';
            }
            setLimitType(detectedType);
            setShowLimitModal(true);
        };
        var handleJsonParsingError = function (event) {
            // Show a friendly toast for JSON parsing errors
            console.error('JSON Parsing Error:', event.detail);
            // The toast will be handled by the component that triggered the request
        };
        window.addEventListener('limitReached', handleLimitReached);
        window.addEventListener('jsonParsingError', handleJsonParsingError);
        return function () {
            window.removeEventListener('limitReached', handleLimitReached);
            window.removeEventListener('jsonParsingError', handleJsonParsingError);
        };
    }, []);
    // Show loading spinner while auth is initializing
    if (loading) {
        return (_jsx("div", __assign({ className: "min-h-screen flex items-center justify-center bg-background" }, { children: _jsx(Card, __assign({ className: "p-8 border-0 shadow-lg" }, { children: _jsxs(CardContent, __assign({ className: "flex flex-col items-center space-y-4" }, { children: [_jsx("div", __assign({ className: "w-16 h-16 bg-gradient-to-r from-primary to-blue-600 rounded-2xl flex items-center justify-center mb-2" }, { children: _jsx(GraduationCap, { className: "h-8 w-8 text-white" }) })), _jsx(LoadingSpinner, { size: "lg" }), _jsxs("div", __assign({ className: "text-center space-y-1" }, { children: [_jsx("p", __assign({ className: "font-medium" }, { children: "Loading LectureLearn..." })), _jsx("p", __assign({ className: "text-sm text-muted-foreground" }, { children: "Preparing your learning environment" }))] }))] })) })) })));
    }
    // Route-wrapped pages
    var HomeScreen = function () { return (_jsx(Welcome, { onNavigate: function (page) { return navigate(page === 'documents' ? routes.documents : routes.exam); } })); };
    var DocumentsScreen = function () { return (_jsx(ProtectedRoute, __assign({ onRequestLogin: function () { return handleAuthAction('login'); } }, { children: _jsx(ModernDocumentSessionLanding, { onSelectSession: function (sessionId) { return navigate(routes.documentAnalysis(sessionId)); } }) }))); };
    var ExamRoute = function () {
        var loc = useLocation();
        return (_jsx(ProtectedRoute, __assign({ onRequestLogin: function () { return handleAuthAction('login'); } }, { children: _jsx(ExamInterface, {}, loc.pathname) })));
    };
    // Document sessions now use enhanced analysis
    var DocumentSessionScreen = function () {
        var id = useParams().id;
        if (!id)
            return _jsx(Navigate, { to: routes.documents, replace: true });
        // Redirect old session URLs to enhanced analysis
        return _jsx(Navigate, { to: routes.documentAnalysis(id), replace: true });
    };
    // Move this outside to prevent recreation on every App render
    var handleUpgrade = function (plan) {
        // TODO: Implement actual upgrade flow (Stripe integration, etc.)
        console.log("Upgrading to ".concat(plan));
        // For now, just close the modal and show a success message
        setShowLimitModal(false);
        // You could redirect to a payment page or show a payment modal here
        alert("Upgrade to ".concat(plan, " plan will be implemented with payment integration."));
    };
    var handleAuthAction = function (mode) {
        // Prevent navigating to auth pages if already authenticated
        if (currentUser) {
            navigate(routes.home);
            return;
        }
        navigate(mode === 'login' ? routes.login : routes.signup);
    };
    // Ensure global navigation works even when a session is active by clearing session state
    var handlePageChange = function (page) {
        if (page === 'welcome')
            navigate(routes.home);
        if (page === 'documents')
            navigate(routes.documents);
        if (page === 'exam')
            navigate(routes.exam);
        if (page === 'exam-boards')
            navigate(routes.examBoards);
    };
    return (_jsx(Layout, { children: _jsxs("div", __assign({ className: "min-h-screen theme-".concat(theme) }, { children: [_jsxs(Routes, { children: [_jsx(Route, { path: routes.login, element: currentUser ? _jsx(Navigate, { to: routes.home, replace: true }) : _jsx(LoginPage, {}) }), _jsx(Route, { path: routes.signup, element: currentUser ? _jsx(Navigate, { to: routes.home, replace: true }) : _jsx(SignupPage, {}) }), _jsx(Route, { path: "/auth/action", element: _jsx(ActionHandler, {}) }), _jsx(Route, { path: "/documents/:documentId/analysis", element: _jsxs("div", __assign({ className: "lg:flex overflow-x-hidden min-w-0 w-full h-screen" }, { children: [_jsx(Navigation, { currentPage: currentPage, onPageChange: handlePageChange, currentUser: currentUser, onAuthAction: handleAuthAction, onShowProfile: function () { return setShowProfile(true); } }), _jsx("div", __assign({ className: "flex-1 flex flex-col min-h-0 overflow-x-hidden main-content" }, { children: _jsx("main", __assign({ className: "flex-1 min-w-0 h-full" }, { children: _jsx(EnhancedDocumentAnalysisScreen, {}) })) }))] })) }), _jsx(Route, { path: "/*", element: _jsxs("div", __assign({ className: "lg:flex overflow-x-hidden min-w-0 w-full" }, { children: [_jsx(Navigation, { currentPage: currentPage, onPageChange: handlePageChange, currentUser: currentUser, onAuthAction: handleAuthAction, onShowProfile: function () { return setShowProfile(true); } }), _jsxs("div", __assign({ className: "flex-1 flex flex-col min-w-0 overflow-x-hidden main-content" }, { children: [_jsx("main", __assign({ className: "flex-1 py-8 px-4 lg:px-8 min-w-0" }, { children: _jsxs(Routes, { children: [_jsx(Route, { path: routes.home, element: _jsx(HomeScreen, {}) }), _jsx(Route, { path: routes.documents, element: _jsx(DocumentsScreen, {}) }), _jsx(Route, { path: routes.exam, element: _jsx(ExamRoute, {}) }), _jsx(Route, { path: routes.cookiePolicy, element: _jsx(CookiePolicy, {}) }), _jsx(Route, { path: routes.examBoards, element: _jsx(ExamBoardsHub, {}) }), _jsx(Route, { path: "/exam-boards/:boardId", element: _jsx(BoardDashboard, {}) }), _jsx(Route, { path: "/exam-boards/:boardId/:contentType", element: _jsx("div", { children: "Content Type Page - Coming Soon" }) }), _jsx(Route, { path: "/exam-boards/:boardId/:subjectId/:contentType", element: _jsx("div", { children: "Subject Content Page - Coming Soon" }) }), _jsx(Route, { path: "/documents/sessions/:id", element: _jsx(DocumentSessionScreen, {}) }), _jsx(Route, { path: "/exam/sessions", element: _jsx(ExamRoute, {}) }), _jsx(Route, { path: "/exam/sessions/:id", element: _jsx(ExamRoute, {}) }), _jsx(Route, { path: "/exam/sessions/:id/practice", element: _jsx(ExamRoute, {}) }), _jsx(Route, { path: "/exam/sessions/:id/review", element: _jsx(ExamRoute, {}) }), _jsx(Route, { path: "/exam/sessions/:id/exam", element: _jsx(ExamRoute, {}) }), _jsx(Route, { path: "*", element: _jsx(NotFound, {}) })] }) })), _jsx("footer", __assign({ className: "border-t border-border dark:border-border bg-white/50 dark:bg-muted/50 backdrop-blur-sm flex-shrink-0 overflow-x-hidden" }, { children: _jsx("div", __assign({ className: "max-w-6xl mx-auto px-4 py-8 text-center text-muted-foreground dark:text-muted-foreground min-w-0" }, { children: _jsx("p", __assign({ className: "truncate" }, { children: "Copyright \u00A9 2025 LectureLearn. All Rights Reserved." })) })) }))] }))] })) })] }), currentUser && (_jsx(UserProfile, { isOpen: showProfile, onClose: function () { return setShowProfile(false); } })), _jsx(LimitReachedModal, { isOpen: showLimitModal, onClose: function () { return setShowLimitModal(false); }, limitType: limitType, currentPlan: currentPlan, onUpgrade: handleUpgrade }), _jsx(CookieConsent, {})] })) }));
};
// Wrap the app with providers
function App() {
    return (_jsx(ErrorBoundary, { children: _jsx(ThemeProvider, { children: _jsx(AuthProvider, { children: _jsx(ToastProvider, { children: _jsx(AppContent, {}) }) }) }) }));
}
export default App;
